import { Box, CssBaseline } from '@mui/material';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { observer } from 'mobx-react-lite';
import { createContext, FC, ReactNode, useEffect, useState } from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';
import 'reflect-metadata';

import { theme } from '~/assets/muiTheme/muiTheme';
import { MFErrorText } from '~/components/reuseable/MFErrorText';
import { i18n } from '~/modules/lang/LangAdapter';
import { LangTypeEnum } from '~/modules/lang/types/Lang';
import { useRootStore } from '~base/hooks/useRootStore';
import { INotification } from '~base/types/BaseTypes';
import { Loader } from '~base/ui/Loader';

import Notification from '../../base/ui/Notification';

export type MFBaseContextPropsType = {
  accessToken: string;
  refreshToken: string;
  notification?: INotification;
  lang?: string;
};

export type MFBaseProviderPropsType = MFBaseContextPropsType & {
  children: ReactNode;
};

export const MFBaseContext = createContext<MFBaseContextPropsType | undefined>(undefined);

// Типы не передаются в родительские проекты, поэтому определяем обязательные пропы
const requiredProps: Partial<keyof MFBaseContextPropsType>[] = ['accessToken', 'refreshToken'];

export const MFBaseProvider: FC<MFBaseProviderPropsType> = observer((props) => {
  const { authStore, langStore, init } = useRootStore();

  const [loading, setLoading] = useState(true);

  const { t } = useTranslation();

  // Effects

  useEffect(() => {
    if (props.accessToken && props.refreshToken) {
      void authStore.setAuthTokens(props.accessToken, props.refreshToken);
    }

    if (props.notification) {
      Notification.init(props.notification);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    if (props.lang) {
      void langStore.changeLang(props.lang as LangTypeEnum);
    }
  }, [props.lang]);

  useEffect(() => {
    void init();
  }, []);

  // Renders

  if (loading) {
    return (
      <Box position="relative" p={3} minHeight="30vh">
        <Loader isAbsolute />
      </Box>
    );
  }

  if (
    !authStore.accessToken ||
    !authStore.refreshToken ||
    requiredProps.some((prop) => props[prop as keyof MFBaseContextPropsType] === undefined)
  ) {
    console.log(t('error.requiredPropsNotFound'));

    return <MFErrorText />;
  }

  const contextValue = {
    ...props,
    // ...
  };

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <I18nextProvider i18n={i18n}>
          <MFBaseContext.Provider value={contextValue}>{props.children}</MFBaseContext.Provider>
        </I18nextProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
});
